import { useCallback } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import { useLocalStorage } from './useLocalStorage'
import { DEFAULT_GROUP_ORDER } from '@/components/tables/columnGroups'

const STORAGE_KEY = 'returns-table-group-order'

export interface UseGroupOrderReturn {
  groupOrder: string[]
  setGroupOrder: (order: string[] | ((prev: string[]) => string[])) => void
  reorderGroups: (activeId: string, overId: string) => void
  resetGroupOrder: () => void
}

export interface UseGroupOrderOptions {
  /** External group order state (for use with useUserState) */
  groupOrder?: string[]
  /** External setter (for use with useUserState) */
  setGroupOrder?: (order: string[] | ((prev: string[]) => string[])) => void
}

/**
 * Hook to manage column group ordering with localStorage persistence.
 * Supports external state management when options are provided.
 */
export function useGroupOrder(options?: UseGroupOrderOptions): UseGroupOrderReturn {
  // Use local storage as fallback if external state not provided
  const [localGroupOrder, setLocalGroupOrder] = useLocalStorage<string[]>(
    STORAGE_KEY,
    DEFAULT_GROUP_ORDER
  )

  // Use external state if provided, otherwise use local storage
  const groupOrder = options?.groupOrder ?? localGroupOrder
  const setGroupOrder = options?.setGroupOrder ?? setLocalGroupOrder

  const reorderGroups = useCallback(
    (activeId: string, overId: string) => {
      if (activeId === overId) return

      const oldIndex = groupOrder.indexOf(activeId)
      const newIndex = groupOrder.indexOf(overId)

      if (oldIndex === -1 || newIndex === -1) return

      setGroupOrder(arrayMove(groupOrder, oldIndex, newIndex))
    },
    [groupOrder, setGroupOrder]
  )

  const resetGroupOrder = useCallback(() => {
    setGroupOrder(DEFAULT_GROUP_ORDER)
  }, [setGroupOrder])

  return {
    groupOrder,
    setGroupOrder,
    reorderGroups,
    resetGroupOrder,
  }
}
